import { observer } from 'mobx-react-lite';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Chip } from 'primereact/chip';
import type { ApplicationSdk } from '../../../core/sdk';
import SystemSettingStore, { ApiToken } from '../stores/SystemSettingStore';

const SettingApiTokens = observer(
    ({ store, sdk }: { store: SystemSettingStore; sdk: ApplicationSdk }) => {
        const showTokenDialog: boolean = !!store.apiTokenPreview;

        const handleOpenCreateDialog = async () => {
            await sdk.getDialogMultiInput({
                title: 'Neuen API-Token erstellen',
                fields: [
                    {
                        key: 'tokenName',
                        label: 'Token-Name',
                        type: 'text',
                        required: true,
                    },
                    {
                        key: 'expiresInDays',
                        label: 'Gültigkeitsdauer (Tage) [optional]',
                        type: 'number',
                        placeholder: 'Immer gültig',
                        suffix: ' Tage',
                        required: false,
                    },
                ],
                message: 'Geben Sie die Details für den neuen API-Token ein.',
                onConfirm: async values => {
                    const { tokenName, expiresInDays } = values as {
                        tokenName: string;
                        expiresInDays?: number;
                    };
                    return store.createApiToken(tokenName.trim(), expiresInDays);
                },
            });
        };

        const copyToClipboard = async (text: string) => {
            try {
                await navigator.clipboard.writeText(text);
                sdk.showSuccessToast('Token in die Zwischenablage kopiert.');
            } catch (err) {
                sdk.showErrorToast('Fehler beim Kopieren in die Zwischenablage.');
            }
        };

        const handleRevokeToken = async (tokenId: number) => {
            const result = await sdk.getDialogDelete({
                title: 'Token sperren',
                message:
                    'Sind Sie sicher, dass Sie dieses Token sperren möchten? Es kann danach nicht mehr verwendet werden.',
                searchWord: 'sperren',
                onConfirm: async () => store.revokeApiToken(tokenId),
            });

            if (result.confirmed) {
                sdk.showSuccessToast('Token erfolgreich gesperrt.');
            } else if (result.error) {
                sdk.showErrorToast(result.error);
            }
        };

        const formatDate = (date: Date | null) => {
            if (!date) return 'Kein Ablauf';
            return new Date(date).toLocaleDateString('de-DE');
        };

        const actionBodyTemplate = (rowData: ApiToken) => {
            // Abgelaufene oder bereits inaktive Token können nicht gesperrt werden
            const canRevoke = rowData.active && !rowData.isExpired;

            return (
                <Button
                    icon="pi pi-ban"
                    className="p-button-rounded p-button-danger p-button-text"
                    onClick={() => handleRevokeToken(rowData.id)}
                    tooltip={
                        rowData.isExpired
                            ? 'Abgelaufene Token können nicht gesperrt werden'
                            : 'Token sperren'
                    }
                    disabled={!canRevoke}
                />
            );
        };

        const statusBodyTemplate = (rowData: ApiToken) => {
            let label = 'Aktiv';
            let backgroundColor = '#d4edda';
            let color = '#155724';
            let borderColor = '#c3e6cb';

            if (rowData.isExpired) {
                label = 'Abgelaufen';
                backgroundColor = '#fff3cd';
                color = '#856404';
                borderColor = '#ffeaa7';
            } else if (!rowData.active) {
                label = 'Inaktiv';
                backgroundColor = '#f8d7da';
                color = '#721c24';
                borderColor = '#f5c6cb';
            }

            return (
                <Chip
                    label={label}
                    style={{
                        backgroundColor,
                        color,
                        border: `1px solid ${borderColor}`,
                    }}
                />
            );
        };

        return (
            <>
                <Card
                    title="API-Token Verwaltung"
                    style={{
                        marginBottom: '1rem',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        borderRadius: '8px',
                    }}
                >
                    <div style={{ marginBottom: '1rem' }}>
                        <Button
                            label="Neuen API-Token erstellen"
                            icon="pi pi-plus"
                            onClick={handleOpenCreateDialog}
                            className="p-button-primary"
                        />
                    </div>
                    <DataTable
                        value={store.apiTokens}
                        emptyMessage="Keine API-Tokens vorhanden"
                    >
                        <Column
                            field="name"
                            header="Name"
                        />
                        <Column
                            field="expires"
                            header="Läuft ab"
                            body={rowData => formatDate(rowData.expires)}
                        />
                        <Column
                            field="active"
                            header="Status"
                            body={statusBodyTemplate}
                        />
                        <Column
                            header="Aktionen"
                            body={actionBodyTemplate}
                            style={{ width: '8rem' }}
                        />
                    </DataTable>
                </Card>
                <Dialog
                    header="API-Token erfolgreich erstellt"
                    visible={showTokenDialog}
                    style={{ width: '650px' }}
                    modal
                    closable={false}
                    onHide={() => store.deleteTokenPreview()}
                >
                    {store.apiTokenPreview && (
                        <div>
                            <div
                                style={{
                                    marginBottom: '1.5rem',
                                    padding: '1rem',
                                    backgroundColor: '#fff3cd',
                                    border: '1px solid #ffeaa7',
                                    borderRadius: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                }}
                            >
                                <i
                                    className="pi pi-exclamation-triangle"
                                    style={{ color: '#856404', fontSize: '1.2rem' }}
                                ></i>
                                <div>
                                    <strong style={{ color: '#856404' }}>Wichtiger Hinweis:</strong>
                                    <div
                                        style={{
                                            color: '#856404',
                                            fontSize: '0.9rem',
                                            marginTop: '0.25rem',
                                        }}
                                    >
                                        Dieser Token wird nur einmal angezeigt und wurde bereits in
                                        die Zwischenablage kopiert. Speichern Sie ihn sicher, da er
                                        später nicht mehr angezeigt werden kann.
                                    </div>
                                </div>
                            </div>

                            <div style={{ marginBottom: '1.5rem' }}>
                                <label
                                    style={{
                                        display: 'block',
                                        marginBottom: '0.75rem',
                                        fontWeight: 'bold',
                                        fontSize: '1.1rem',
                                    }}
                                >
                                    🔑 Ihr neuer API-Token:
                                </label>
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: '0.75rem',
                                        padding: '1rem',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '6px',
                                        border: '2px solid #dee2e6',
                                    }}
                                >
                                    <InputText
                                        value={store.apiTokenPreview.token}
                                        readOnly
                                        style={{
                                            flex: 1,
                                            fontFamily:
                                                'Consolas, Monaco, "Courier New", monospace',
                                            fontSize: '0.9rem',
                                            backgroundColor: 'white',
                                        }}
                                    />
                                    <Button
                                        label="Kopieren"
                                        icon="pi pi-copy"
                                        className="p-button-success"
                                        onClick={() => copyToClipboard(store.apiTokenPreview.token)}
                                        style={{ whiteSpace: 'nowrap' }}
                                    />
                                </div>
                            </div>

                            <div
                                style={{
                                    fontSize: '0.95rem',
                                    color: '#6c757d',
                                    backgroundColor: '#f8f9fa',
                                    padding: '1rem',
                                    borderRadius: '6px',
                                    border: '1px solid #e9ecef',
                                }}
                            >
                                <h4 style={{ margin: '0 0 0.75rem 0', color: '#495057' }}>
                                    Token-Details:
                                </h4>
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '120px 1fr',
                                        gap: '0.5rem',
                                    }}
                                >
                                    <strong>Name:</strong>{' '}
                                    <span>{store.apiTokenPreview.tokenData.name}</span>
                                    <strong>Erstellt:</strong>{' '}
                                    <span>
                                        {formatDate(store.apiTokenPreview.tokenData.created)}
                                    </span>
                                    <strong>Läuft ab:</strong>{' '}
                                    <span>
                                        {formatDate(store.apiTokenPreview.tokenData.expires)}
                                    </span>
                                    <strong>Status:</strong>{' '}
                                    <Chip
                                        label={
                                            store.apiTokenPreview.tokenData.isExpired
                                                ? 'Abgelaufen'
                                                : 'Aktiv'
                                        }
                                        style={{
                                            backgroundColor: store.apiTokenPreview.tokenData
                                                .isExpired
                                                ? '#fff3cd'
                                                : '#d4edda',
                                            color: store.apiTokenPreview.tokenData.isExpired
                                                ? '#856404'
                                                : '#155724',
                                            border: `1px solid ${store.apiTokenPreview.tokenData.isExpired ? '#ffeaa7' : '#c3e6cb'}`,
                                            fontSize: '0.8rem',
                                            height: '1.5rem',
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginTop: '1.5rem',
                            paddingTop: '1rem',
                            borderTop: '1px solid #dee2e6',
                        }}
                    >
                        <Button
                            label="Erneut kopieren"
                            icon="pi pi-copy"
                            className="p-button-outlined p-button-success"
                            onClick={() =>
                                store.apiTokenPreview &&
                                copyToClipboard(store.apiTokenPreview.token)
                            }
                        />
                        <Button
                            label="Verstanden, schließen"
                            icon="pi pi-check"
                            className="p-button-primary"
                            onClick={() => store.deleteTokenPreview()}
                        />
                    </div>
                </Dialog>
            </>
        );
    }
);

export default SettingApiTokens;
